import { Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import React, { useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DynamicForm } from '@/console/features/dynamic-form/dynamic-form';
import { LocalLicenseSelect } from '@/console/features/licenses/components/local-license-select/local-license-select';
import { TCreateProjectFormProps } from '@/console/features/projects/components/create-project/create-project-form.types';
import { TCreateProjectInputSchema } from '@/console/features/projects/components/create-project/create-project.types';
import { createProjectInputSchema } from '@/console/features/projects/components/create-project/create-project.validation';
import { FormSelectSecret } from '@/console/features/vaults/components/form-select-secret/form-select-secret';
import { Alert } from '@/shared/components/ui/alert/alert';
import { FormDropzone } from '@/shared/components/ui/form/components/file-dropzone/file-dropzone';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { useCustomForm } from '@/shared/components/ui/form/context/form-context';
import { FormContent } from '@/shared/components/ui/form/form.styled';
import { GroupSelect } from '@/shared/features/groups/components/group-select/group-select';
import { TagSelect } from '@/shared/features/tags/components/tag-select/tag-select';
import { useDynamicForm } from '@/shared/hooks/use-dynamic-form/use-dynamic-form';
import {
  useVendor,
  useVendorOptions,
} from '@/shared/hooks/use-vendor/use-vendor';
import { mapIdeOptionsWithInfo } from '@/shared/hooks/use-vendor/use-vendor.utils';
import { ProjectTypeEnum } from '@/shared/types/types.generated';

export const CreateProjectForm: React.FC<TCreateProjectFormProps> = ({
  disableVendorSelect,
  groupId,
}) => {
  const { t } = useTranslation();
  const { setValue, control, resetField, trigger } =
    useFormContext<TCreateProjectInputSchema>();
  const { isSubmitting } = useFormState<TCreateProjectInputSchema>();
  const {
    getVendorOptions,
    isLoading: vendorLoading,
    getIdeOptionsForVendor,
    getProjectTypesOptions,
    getProjectTypeFromFileExtensions,
  } = useVendorOptions();
  const { getExtensionsForIde, getFileType } = useVendor();
  const { createDynamicSchema } = useDynamicForm();
  const { schema: activeSchema, updateSchema, resetSchema } = useCustomForm();
  const [vendorId, ideConfigId, projectType, fileName] = useWatch({
    control,
    name: ['vendorId', 'ideConfigId', 'projectType', 'fileName'],
  });

  const projectTypes = getProjectTypesOptions({ vendorId, ideConfigId });
  const fileExtensions = getExtensionsForIde(
    vendorId,
    ideConfigId,
    projectType,
  );
  const disableDropzone = !vendorId || !ideConfigId;
  const ideOptions = mapIdeOptionsWithInfo(getIdeOptionsForVendor(vendorId));

  const extension = fileName?.split('.').pop();
  const additionalInputs = getFileType({
    vendorId,
    ideConfigId,
    projectType,
    extension,
  })?.additionalInputs;

  getProjectTypeFromFileExtensions(vendorId, ideConfigId, '');

  // Keep the dynamic metaData fields in sync with the selected project type
  // (and the file extension when several share that project type).
  useEffect(() => {
    resetField('metaData');

    if (!additionalInputs?.length) {
      resetSchema();
      return;
    }

    const dynamicSchema = createDynamicSchema(additionalInputs);
    updateSchema(
      createProjectInputSchema.extend({ metaData: dynamicSchema.default({}) }),
    );
  }, [vendorId, ideConfigId, projectType, extension]);

  // Re-validate metaData once the active schema (and therefore the resolver)
  // has actually changed, so the submit button reflects the new rules.
  useEffect(() => {
    void trigger('metaData');
  }, [activeSchema]);

  const resetFile = () => {
    resetField('file');
    resetField('fileName');
  };

  const handleVendorChange = (event: SelectChangeEvent<unknown>) => {
    resetFile();

    const vendorId = event.target.value as string;
    const ideOptions = getIdeOptionsForVendor(vendorId) ?? [];

    if (ideOptions?.length > 1) {
      resetField('ideConfigId');
      return;
    }

    const ideConfigId = ideOptions[0]?.value;
    setValue('ideConfigId', ideConfigId);
  };

  const handleProjectTypeDetect = (file: File) => {
    const fileExtension = file.name.split('.').pop();

    if (!fileExtension) {
      return;
    }

    const _projectType = getProjectTypeFromFileExtensions(
      vendorId,
      ideConfigId,
      fileExtension,
    );

    if (!_projectType && projectTypes?.length > 1) {
      return;
    }

    const projectType = _projectType ?? ProjectTypeEnum.Standard;
    setValue('projectType', projectType, { shouldValidate: true });
  };

  const handleFileSelection = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    handleProjectTypeDetect(files[0]);

    setValue('fileName', files[0].name, { shouldValidate: true });
  };

  return (
    <FormContent overflow="auto">
      <FormInput<TCreateProjectInputSchema>
        name="name"
        required
        label={t('projects:form.fields.name.label')}
        placeholder={t('projects:form.fields.name.placeholder')}
      />
      <FormInput<TCreateProjectInputSchema>
        name="description"
        label={t('projects:form.fields.description.label')}
        placeholder={t('projects:form.fields.description.placeholder')}
      />
      <Stack direction="row" gap="0.75rem">
        <FormSelect<TCreateProjectInputSchema>
          name="vendorId"
          label={t('common:vendor')}
          options={getVendorOptions()}
          disabled={vendorLoading || disableVendorSelect}
          onChange={handleVendorChange}
          required
        />
        <FormSelect<TCreateProjectInputSchema>
          name="ideConfigId"
          label={t('common:ide')}
          disabled={!vendorId || disableVendorSelect}
          options={ideOptions}
          onChange={resetFile}
          required
        />
      </Stack>
      {projectTypes.length > 1 && (
        <FormSelect<TCreateProjectInputSchema>
          name="projectType"
          label={t('projects:form.fields.project_type.label')}
          options={projectTypes}
          required
        />
      )}
      {additionalInputs && (
        <DynamicForm options={additionalInputs} formNamePrefix="metaData" />
      )}
      <LocalLicenseSelect<TCreateProjectInputSchema>
        name="attachedLicenses"
        vendorId={vendorId}
        ideConfigId={ideConfigId}
        useProductAsValue
      />
      {projectType === ProjectTypeEnum.Generic && (
        <Alert
          color="blue"
          text={t('projects:form.fields.generic_project_hint')}
        />
      )}
      <GroupSelect<TCreateProjectInputSchema>
        name="groupId"
        getItemId={(value) => value}
        label={t('groups:group')}
      />
      <TagSelect<TCreateProjectInputSchema> name="tags" entity="Project" />
      <FormDropzone<TCreateProjectInputSchema>
        name="file"
        required
        disabled={isSubmitting || disableDropzone}
        acceptedFileTypes={fileExtensions}
        label={t('projects:form.fields.upload_project')}
        maxFiles={1}
        onFilesSelected={handleFileSelection}
      />
      <FormSelectSecret<TCreateProjectInputSchema>
        groupId={groupId}
        allowCreateSecret
      />
    </FormContent>
  );
};
